import { ref } from 'vue'
import { useAuthStore } from '../stores/oauthStore'

const VITE_API_BASE_URL = import.meta.env.VITE_API_BASE_URL
const VITE_API_KEY = import.meta.env.VITE_API_KEY
const TOKEN_URL = `${VITE_API_BASE_URL}/oauth/v2/token`
const REFRESH_INTERVAL = 25 * 60 * 1000 // 25 minutes in milliseconds

export const defaultGrantType = 'https://com.sqills.s3.oauth.public'
export const bookingGrantType = 'https://com.sqills.s3.oauth.booking'
const userActive = ref(true)

export async function fetchOAuthToken(
  grantType: string = defaultGrantType
): Promise<string> {
  const authStore = useAuthStore()

  // Check if a token already exists
  if (
    authStore.token &&
    authStore.tokenExpirationTime &&
    authStore.tokenExpirationTime > Date.now()
  ) {
    return authStore.token
  }

  const data = {
    grant_type: grantType,
    code: import.meta.env.VITE_GRANT_TYPE_INTERNET,
  }

  const response = await fetch(TOKEN_URL, {
    method: 'POST',
    headers: {
      Authorization: `Basic ${VITE_API_KEY}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })

  if (!response.ok) {
    throw new Error('Failed to fetch token')
  }

  const responseData = await response.json()
  const token = responseData.access_token

  if (token === null) {
    throw new Error('Failed to retrieve access token')
  }

  const tokenExpirationTime = Date.now() + REFRESH_INTERVAL

  // Store the token and its expiration time
  authStore.setToken(token, tokenExpirationTime)

  return token
}

// Function to refresh the token if needed
async function refreshTokenIfNeeded() {
  const authStore = useAuthStore()
  if (!authStore.token || !userActive.value) return
  if (authStore.tokenExpirationTime && Date.now() >= authStore.tokenExpirationTime - 5 * 60 * 1000) {
    try {
      if (authStore.isBookingToken && authStore.currentBookingNumber) {
        const email = localStorage.getItem(`email_${authStore.currentBookingNumber}`) || ''
        if (email) {
          const token = await fetchBookingToken(authStore.currentBookingNumber, email)
          const tokenExpirationTime = Date.now() + REFRESH_INTERVAL
          authStore.setToken(token, tokenExpirationTime, 'booking', authStore.currentBookingNumber)
        } else {
          console.warn('Cannot refresh booking token: missing email for booking number', authStore.currentBookingNumber)
          await fetchOAuthToken()
        }
      } else {
        await fetchOAuthToken()
      }
    } catch (error) {
      console.error('Failed to refresh token:', error)
      if (authStore.tokenExpirationTime && Date.now() >= authStore.tokenExpirationTime) {
        authStore.clearToken()
      }
    }
  }
}

// Function to track user activity
function trackUserActivity() {
  userActive.value = true // Update userActive to true on activity
  setTimeout(() => {
    userActive.value = false
  }, REFRESH_INTERVAL)
}

// Export function to initialize OAuth service
export function initializeOAuthService() {
  window.addEventListener('mousemove', trackUserActivity)
  window.addEventListener('keydown', trackUserActivity)

  // Set up interval to refresh the token
  setInterval(refreshTokenIfNeeded, REFRESH_INTERVAL)

  // Initial token fetch
  return fetchOAuthToken()
    .catch((error) => {
      console.error('Failed to fetch initial token:', error)
    })
}

export async function fetchBookingToken(
  bookingNumber: string,
  email: string
): Promise<string> {
  const data = {
    grant_type: bookingGrantType,
    booking_number: bookingNumber,
    email: email,
    code: import.meta.env.VITE_GRANT_TYPE_INTERNET,
  }

  const response = await fetch(TOKEN_URL, {
    method: 'POST',
    headers: {
      Authorization: `Basic ${VITE_API_KEY}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })

  if (!response.ok) {
    throw new Error('Failed to fetch booking token')
  }

  const responseData = await response.json()
  const token = responseData.access_token

  if (token === null) {
    throw new Error('Failed to retrieve booking access token')
  }

  return token
}

export async function exchangeToken(
  subjectToken: string,
  options?: {
    selectedOfficeName?: string
    selectedContractCode?: string
    contractAccessCode?: string
    organizationAccessCode?: string
  }
): Promise<string> {
  const data = {
    grant_type: 'urn:ietf:params:oauth:grant-type:token-exchange',
    subject_token: subjectToken,
    subject_token_type: 'urn:ietf:params:oauth:token-type:access_token',
    ...options
  }

  const response = await fetch(TOKEN_URL, {
    method: 'POST',
    headers: {
      Authorization: `Basic ${VITE_API_KEY}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })

  if (!response.ok) {
    throw new Error('Failed to exchange token')
  }

  const responseData = await response.json()
  const token = responseData.access_token

  if (!token) {
    throw new Error('Failed to retrieve exchanged access token')
  }

  const authStore = useAuthStore()
  const tokenExpirationTime = Date.now() + REFRESH_INTERVAL
  authStore.setToken(token, tokenExpirationTime)

  return token
}
